import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// UI store for managing global UI state
const useUIStore = create(
  persist(
    immer((set, get) => ({
      // Language state
      language: "en", // 'en' | 'ar'

      // Layout state
      layout: {
        sidebarOpen: true,
        sidebarCollapsed: false,
        headerHeight: 64,
        sidebarWidth: 280,
        collapsedSidebarWidth: 56,
      },

      // Loading states
      globalLoading: false,
      loadingStates: {}, // { [key]: boolean }

      // Notification state
      notifications: [],
      maxNotifications: 5,

      // Modal state
      modals: {}, // { [modalId]: { open: boolean, data: any } }

      // Snackbar state
      snackbar: {
        open: false,
        message: "",
        severity: "info", // 'success' | 'error' | 'warning' | 'info'
        autoHideDuration: 6000,
      },

      // Breadcrumb state
      breadcrumbs: [],

      // Page state
      pageTitle: "",
      pageSubtitle: "",

      // Actions for language
      setLanguage: (language) =>
        set((state) => {
          state.language = language;
        }),

      // Actions for layout
      setSidebarOpen: (open) =>
        set((state) => {
          state.layout.sidebarOpen = open;
        }),

      toggleSidebar: () =>
        set((state) => {
          state.layout.sidebarOpen = !state.layout.sidebarOpen;
        }),

      setSidebarCollapsed: (collapsed) =>
        set((state) => {
          state.layout.sidebarCollapsed = collapsed;
        }),

      toggleSidebarCollapsed: () =>
        set((state) => {
          state.layout.sidebarCollapsed = !state.layout.sidebarCollapsed;
        }),

      // Actions for loading states
      setGlobalLoading: (loading) =>
        set((state) => {
          state.globalLoading = loading;
        }),

      setLoadingState: (key, loading) =>
        set((state) => {
          state.loadingStates[key] = loading;
        }),

      clearLoadingState: (key) =>
        set((state) => {
          delete state.loadingStates[key];
        }),

      clearAllLoadingStates: () =>
        set((state) => {
          state.loadingStates = {};
        }),

      // Actions for notifications
      addNotification: (notification) =>
        set((state) => {
          const newNotification = {
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            ...notification,
          };

          state.notifications.unshift(newNotification);

          // Limit number of notifications
          if (state.notifications.length > state.maxNotifications) {
            state.notifications = state.notifications.slice(
              0,
              state.maxNotifications
            );
          }
        }),

      removeNotification: (id) =>
        set((state) => {
          state.notifications = state.notifications.filter((n) => n.id !== id);
        }),

      clearNotifications: () =>
        set((state) => {
          state.notifications = [];
        }),

      markNotificationAsRead: (id) =>
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (notification) {
            notification.read = true;
          }
        }),

      // Actions for modals
      openModal: (modalId, data = null) =>
        set((state) => {
          state.modals[modalId] = { open: true, data };
        }),

      closeModal: (modalId) =>
        set((state) => {
          if (state.modals[modalId]) {
            state.modals[modalId].open = false;
          }
        }),

      closeAllModals: () =>
        set((state) => {
          Object.keys(state.modals).forEach((modalId) => {
            state.modals[modalId].open = false;
          });
        }),

      // Actions for snackbar
      showSnackbar: (message, severity = "info", autoHideDuration = 6000) =>
        set((state) => {
          state.snackbar = {
            open: true,
            message,
            severity,
            autoHideDuration,
          };
        }),

      hideSnackbar: () =>
        set((state) => {
          state.snackbar.open = false;
        }),

      // Actions for breadcrumbs
      setBreadcrumbs: (breadcrumbs) =>
        set((state) => {
          state.breadcrumbs = breadcrumbs;
        }),

      addBreadcrumb: (breadcrumb) =>
        set((state) => {
          state.breadcrumbs.push(breadcrumb);
        }),

      clearBreadcrumbs: () =>
        set((state) => {
          state.breadcrumbs = [];
        }),

      // Actions for page state
      setPageTitle: (title, subtitle = "") =>
        set((state) => {
          state.pageTitle = title;
          state.pageSubtitle = subtitle;
        }),

      clearPageTitle: () =>
        set((state) => {
          state.pageTitle = "";
          state.pageSubtitle = "";
        }),

      // Getters
      getModalState: (modalId) => {
        const { modals } = get();
        return modals[modalId] || { open: false, data: null };
      },

      isModalOpen: (modalId) => {
        const { modals } = get();
        return modals[modalId]?.open || false;
      },

      getLoadingState: (key) => {
        const { loadingStates } = get();
        return loadingStates[key] || false;
      },

      isAnyLoading: () => {
        const { globalLoading, loadingStates } = get();
        return globalLoading || Object.values(loadingStates).some(Boolean);
      },

      getUnreadNotificationCount: () => {
        const { notifications } = get();
        return notifications.filter((n) => !n.read).length;
      },

      // Reset UI state (useful for logout)
      resetUIState: () =>
        set((state) => {
          state.globalLoading = false;
          state.loadingStates = {};
          state.notifications = [];
          state.modals = {};
          state.snackbar.open = false;
          state.breadcrumbs = [];
          state.pageTitle = "";
          state.pageSubtitle = "";
        }),
    })),
    {
      name: "juzoor-ui-store",
      storage: createJSONStorage(() => localStorage),
      // Persist user preferences
      partialize: (state) => ({
        language: state.language,
        layout: {
          sidebarCollapsed: state.layout.sidebarCollapsed,
        },
      }),
    }
  )
);

export default useUIStore;
